"use client";

import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";

const createThemeToast = (message, isDark) => {
  const toast = document.createElement("div");

  const baseStyles = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 24px;
    border-radius: 12px;
    z-index: 10000;
    font-size: 14px;
    max-width: 300px;
    text-align: center;
    font-family: inherit;
    border: 1px solid;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
  `;

  if (isDark) {
    toast.style.cssText =
      baseStyles +
      `
      background: #181a20;
      color: #ffffff;
      border-color: #df27df;
      box-shadow: 0 4px 12px rgba(223, 39, 223, 0.2);
    `;
  } else {
    toast.style.cssText =
      baseStyles +
      `
      background: #ffffff;
      color: #1a1a1a;
      border-color: #ff1cf7;
      box-shadow: 0 4px 12px rgba(255, 28, 247, 0.2);
    `;
  }

  toast.textContent = message;
  toast.style.opacity = "0";
  toast.style.transform = "translateX(-50%) translateY(20px)";
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.style.transition = "all 0.3s ease-out";
    toast.style.opacity = "1";
    toast.style.transform = "translateX(-50%) translateY(0)";
  });

  setTimeout(() => {
    toast.style.transition = "all 0.3s ease-in";
    toast.style.opacity = "0";
    toast.style.transform = "translateX(-50%) translateY(-20px)";
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 300);
  }, 3000);
};

const createThemeModal = (content, isDark) => {
  const modal = document.createElement("div");

  const modalBaseStyles = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    backdrop-filter: blur(4px);
  `;

  const contentBaseStyles = `
    padding: 0;
    border-radius: 12px;
    max-width: 320px;
    text-align: left;
    font-family: inherit;
    border: 1px solid;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  `;

  modal.style.cssText = modalBaseStyles;

  const modalContent = document.createElement("div");

  if (isDark) {
    modalContent.style.cssText =
      contentBaseStyles +
      `
      background: #181a20;
      color: #ffffff;
      border-color: #df27df;
    `;
  } else {
    modalContent.style.cssText =
      contentBaseStyles +
      `
      background: #ffffff;
      color: #1a1a1a;
      border-color: #ff1cf7;
    `;
  }

  modalContent.innerHTML = content;
  modal.appendChild(modalContent);

  modal.onclick = (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  };

  return { modal, modalContent };
};

export default function BlogSocialShare({ post, url }) {
  const isdark = useIsDark();

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first (works on HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Fallback method for older browsers or non-HTTPS
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      textArea.readOnly = false; // Must be writable for copy to work

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      return successful;
    } catch (err) {
      console.error("Clipboard copy failed:", err);

      // Final fallback - try simple method
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        return true;
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
        return false;
      }
    }
  };

  const openNativeApp = (appUrl, fallbackUrl) => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    if (isAndroid || isIOS) {
      // Try to open native app
      window.location.href = appUrl;

      // Fallback to web after a delay
      setTimeout(() => {
        if (!document.hidden) {
          window.open(fallbackUrl, "_blank");
        }
      }, 2000);
    } else {
      // Desktop: open web version
      window.open(fallbackUrl, "_blank");
    }
  };

  const handleSocialShare = (platform) => {
    const categories = (post.categories ?? []).map((c) => c.title);
    const hashtags = categories.map((c) => c.replace(/\s+/g, "")).join(" #");

    // Check if Web Share API is supported (mobile devices)
    // const canUseWebShare =
    //   navigator.share && /android|iphone|ipad|ipod/i.test(navigator.userAgent);

    const socialConfigs = {
      facebook: {
        caption: `📖 Just read an insightful article: "${post.title}"\n\n✨ Great content from ICIEOS about ${categories.join(", ")}. Worth checking out!\n\n${url}\n\n#ICIEOS #${hashtags}`,
        customAction: async () => {
          const shareText = `📖 Just read an insightful article: "${post.title}"\n\n✨ Great content from ICIEOS about ${categories.join(", ")}. Worth checking out!\n\n#ICIEOS #${hashtags}`;

          // Always copy to clipboard first
          await copyToClipboard(shareText);

          createThemeToast("Caption copied! Opening Facebook...", isdark);

          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            "_blank",
            "width=600,height=400"
          );
        },
      },

      twitter: {
        customAction: async () => {
          const shareText = `📝 "${post.title}"\n\n💡 Great insights from @ICIEOS! Worth reading 👇\n\n#TechInsights #${hashtags}`;

          // Always copy to clipboard first
          await copyToClipboard(shareText);

          createThemeToast("Caption copied! Opening X...", isdark);
          window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`,
            "_blank"
          );
        },
      },

      linkedin: {
        caption: `📝 "${post.title}"\n\nI found this article particularly insightful. The team at ICIEOS shares valuable perspectives on ${categories.join(", ")}.\n\n💡 Key insights that caught my attention and worth sharing with my network.\n\n${url}\n\n#ICIEOS #ProfessionalDevelopment #${hashtags}`,
        customAction: async () => {
          const shareText = `📝 "${post.title}"\n\nI found this article particularly insightful. The team at ICIEOS shares valuable perspectives on ${categories.join(", ")}.\n\n💡 Key insights that caught my attention and worth sharing with my network.\n\n#ICIEOS #ProfessionalDevelopment #${hashtags}`;

          await copyToClipboard(`${shareText}\n\n${url}`);

          // Create options modal
          const borderColor = isdark ? "#38434f" : "#e5e7eb";
          const cardBg = isdark ? "#262626" : "#f9fafb";
          const hoverBg = isdark ? "#38434f" : "#f3f4f6";
          const textSecondary = isdark ? "#b0b3b8" : "#6b7280";

          const modalContent = `
              <div style="padding: 16px 20px; border-bottom: 1px solid ${borderColor};">
                <div style="display: flex; align-items: center; justify-content: space-between;">
                  <div style="display: flex; align-items: center;">
                    <img src="/assets/blogPage/linkedin.svg" style="width: 24px; height: 24px; margin-right: 8px;" />
                    <span style="font-weight: 600; font-size: 16px;">LinkedIn</span>
                  </div>
                  <button id="linkedin-close" style="background: none; border: none; color: ${textSecondary}; font-size: 20px; cursor: pointer; padding: 4px;">×</button>
                </div>
              </div>
              
              <div style="padding: 0;">
                <div style="padding: 16px 20px; background: ${cardBg}; margin: 8px 12px; border-radius: 8px;">
                  <h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${post.title}</h4>
                  <p style="margin: 0; color: ${textSecondary}; font-size: 14px;">${url}</p>
                </div>
                
                <div style="padding: 8px;">
                  <button id="linkedin-post" style="display: flex; align-items: center; width: 100%; padding: 12px 16px; margin-bottom: 4px; background: none; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; text-align: left;" onmouseover="this.style.background='${hoverBg}'" onmouseout="this.style.background='none'">
                    <svg width="20" height="20" style="margin-right: 12px;" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H9v-9h5v9zm2.5-9.5V16H15V7.5h1.5zm-8 0V16H7V7.5h1.5z"/>
                    </svg>
                    Share in a post
                  </button>
                  <button id="linkedin-message" style="display: flex; align-items: center; width: 100%; padding: 12px 16px; margin-bottom: 8px; background: none; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; text-align: left;" onmouseover="this.style.background='${hoverBg}'" onmouseout="this.style.background='none'">
                    <svg width="20" height="20" style="margin-right: 12px;" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    Send as private message
                  </button>
                </div>
              </div>
            `;

          const { modal } = createThemeModal(modalContent, isdark);
          document.body.appendChild(modal);

          document.getElementById("linkedin-post").onclick = () => {
            window.open(
              `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
              "_blank"
            );
            document.body.removeChild(modal);
          };

          document.getElementById("linkedin-message").onclick = () => {
            // LinkedIn messaging with preloaded content
            const messageText = `${shareText}\n\n${url}`;

            // Copy message to clipboard first
            copyToClipboard(messageText);

            // Try LinkedIn's messaging URL with body parameter (works on some versions)
            const linkedinMsgUrl = `https://www.linkedin.com/messaging/compose?body=${encodeURIComponent(messageText)}`;

            // Unified approach for all devices
            // Try the LinkedIn web URL with body parameter first
            const newWindow = window.open(linkedinMsgUrl, "_blank");

            // If that fails, fallback to regular messaging page
            if (!newWindow) {
              window.open("https://www.linkedin.com/messaging/", "_blank");
            }

            document.body.removeChild(modal);
          };

          document.getElementById("linkedin-close").onclick = () => {
            document.body.removeChild(modal);
          };

          modal.onclick = (e) => {
            if (e.target === modal) document.body.removeChild(modal);
          };
        },
      },

      whatsapp: {
        customAction: async () => {
          const shareText = `📖 Hey! Just read this amazing article: "${post.title}"\n\n💡 Really insightful content from ICIEOS. Thought you might find it interesting!`;

          await copyToClipboard(shareText + "\n\n" + url);

          // direct to WhatsApp Web
          createThemeToast("Caption copied! Opening WhatsApp...", isdark);

          window.open(
            `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + "\n\n" + url)}`,
            "_blank"
          );
        },
      },

      discord: {
        customAction: async () => {
          const discordMessage = `📖 **${post.title}**\n\n🚀 Just published a new blog post! Check out these insights on ${categories.join(", ")}.\n\n${url}\n\n*What do you think? Share your thoughts below!*`;

          // Always copy to clipboard first
          await copyToClipboard(discordMessage);

          copyToClipboard(discordMessage);
          createThemeToast("Message copied! Opening Discord...", isdark);

          window.open("https://discord.com/channels/@me", "_blank");
        },
      },
    };

    const config = socialConfigs[platform];
    if (!config) return;

    if (config.customAction) {
      config.customAction();
      return;
    }

    if (config.caption) {
      copyToClipboard(config.caption);

      // Use toast notification instead of alert
      createThemeToast("Caption copied to clipboard!", isdark);
    }

    if (config.nativeUrl && config.webUrl) {
      openNativeApp(config.nativeUrl, config.webUrl);
    } else if (config.webUrl) {
      window.open(config.webUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex justify-start w-full items-center gap-[18px] mt-15">
      <p
        className={twMerge(
          "font-alexandria font-normal text-sm sm:text-base md:text-lg lg:text-xl xl:text-[22px] 4xl:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-[#9C00BB] uppercase",
          isdark ? "to-[#FFFFFF]" : "to-[#1A1A1A]"
        )}
      >
        Share :
      </p>
      <div className="flex gap-[14px]">
        <div onClick={() => handleSocialShare("facebook")}>
          <IconCircle icon="/assets/blogPage/fb.svg" />
        </div>
        <div onClick={() => handleSocialShare("twitter")}>
          <IconCircle icon="/assets/blogPage/x.svg" />
        </div>
        <div onClick={() => handleSocialShare("linkedin")}>
          <IconCircle icon="/assets/blogPage/linkedin.svg" />
        </div>
        <div onClick={() => handleSocialShare("whatsapp")}>
          <IconCircle icon="/assets/blogPage/whatsapp.svg" />
        </div>
        <div onClick={() => handleSocialShare("discord")}>
          <IconCircle icon="/assets/blogPage/discord.svg" />
        </div>
      </div>
    </div>
  );
}

function IconCircle({ icon }) {
  const isdark = useIsDark();

  return (
    <div
      className={twMerge(
        "p-px rounded-full bg-linear-to-tr",
        isdark
          ? " from-[#0F0F0F] to-[#74748D]"
          : "from-[#FFFFFF00] to-[#FFFFFF4D]"
      )}
    >
      <div
        className={twMerge(
          "flex justify-center items-center rounded-full w-6 h-6 sm:w-7 sm:h-7 md:w-[30px] md:h-[30px] lg:w-9 lg:h-9 laptop:w-[32px] laptop:h-[32px] 2xl:w-[36px] 2xl:h-[36px] 3xl:w-[40px] 3xl:h-[40px] 4xl:w-[48px] 4xl:h-[48px] cursor-pointer",
          isdark
            ? "bg-linear-to-br from-[#0F0F0F] to-[#232020] hover:from-[#222222] hover:to-[#df27df44]"
            : "bg-[#D2D2E6] hover:bg-[#ff1cf722]"
        )}
      >
        <img
          src={icon}
          width={28}
          height={28}
          alt="social media"
          quality={70}
          className="object-contain
          w-[14px] h-[14px]
          sm:w-4 sm:h-4
          md:w-[18px] md:h-[18px]
          lg:w-5 lg:h-5
          laptop:w-5 laptop:h-5
          2xl:w-[22px] 2xl:h-[22px]
          3xl:w-6 3xl:h-6
          4xl:w-7 4xl:h-7"
        />
      </div>
    </div>
  );
}
